import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, TextField, Button, Alert, CircularProgress, useTheme } from '@mui/material';
import { ContactFormData, FormSubmissionState } from '../../types';

/**
 * Contact form component with validation
 * Uses stub submission (simulates API call)
 */
export const ContactForm: React.FC = () => {
  const theme = useTheme();
  const [submissionState, setSubmissionState] = useState<FormSubmissionState>({
    isSubmitting: false,
    isSuccess: false,
    isError: false,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>();

  const onSubmit = async (_data: ContactFormData) => {
    setSubmissionState({
      isSubmitting: true,
      isSuccess: false,
      isError: false,
    });

    try {
      // Stub submission - simulate API call
      // TODO: Replace with actual API call when backend is ready
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSubmissionState({
        isSubmitting: false,
        isSuccess: true,
        isError: false,
      });

      // Reset form after successful submission
      reset();

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmissionState((prev) => ({ ...prev, isSuccess: false }));
      }, 5000);
    } catch {
      setSubmissionState({
        isSubmitting: false,
        isSuccess: false,
        isError: true,
        errorMessage: 'Failed to send message. Please try again.',
      });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
      {/* Success Alert */}
      {submissionState.isSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Thank you for your message! We&apos;ll get back to you within 24 hours.
        </Alert>
      )}

      {/* Error Alert */}
      {submissionState.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {submissionState.errorMessage}
        </Alert>
      )}

      <TextField
        {...register('name', { required: 'Name is required' })}
        label="Name"
        fullWidth
        required
        error={!!errors.name}
        helperText={errors.name?.message}
        sx={{ mb: 2.5 }}
      />

      <TextField
        {...register('email', {
          required: 'Email is required',
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: 'Invalid email address',
          },
        })}
        label="Email"
        type="email"
        fullWidth
        required
        error={!!errors.email}
        helperText={errors.email?.message}
        sx={{ mb: 2.5 }}
      />

      <TextField
        {...register('subject', { required: 'Subject is required' })}
        label="Subject"
        fullWidth
        required
        error={!!errors.subject}
        helperText={errors.subject?.message}
        sx={{ mb: 2.5 }}
      />

      <TextField
        {...register('message', { required: 'Message is required' })}
        label="Message"
        multiline
        rows={5}
        fullWidth
        required
        error={!!errors.message}
        helperText={errors.message?.message}
        sx={{ mb: 3 }}
      />

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={submissionState.isSubmitting}
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: 1.5,
          fontWeight: 600,
          '&:hover': {
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
          },
          '&:disabled': {
            background: theme.palette.action.disabledBackground,
          },
        }}
      >
        {submissionState.isSubmitting ? (
          <CircularProgress size={24} color="inherit" />
        ) : (
          'Send Message'
        )}
      </Button>
    </Box>
  );
};
